import { Point } from "openseadragon";
import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import Modal from "react-modal";
import { useDispatch } from "react-redux";

import { User } from "pwapi";

import {
  RegionOfInterestAnnotation,
  ScaleInfo,
  ViewerMode,
  XYPoint,
} from "custom-models/ViewerTypes";

import { changeViewerMode } from "shared/actions/slideDetailActions";
import { LESION_LINE_TYPE_HIERARCHY } from "shared/reducers/aiReducer";
import {
  createRegionOfInterestAnnotation,
  extendRegionOfInterestAnnotation,
} from "shared/utils/annotations";

import LesionLineTypeSelectionDialog from "./LesionLineTypeSelectionDialog";
import RegionOfInterest from "./RegionOfInterest";
import ViewerFacade from "../ViewerFacade";

const THRESHOLD = 25;

interface RegionOfInterestToolProps {
  lesionLineType: string;
  scaleInfo: ScaleInfo;
  viewerFacade: ViewerFacade;
  user: User;
  onAnnotationComplete: (ann: RegionOfInterestAnnotation) => void;
}

const RegionOfInterestTool = (props: RegionOfInterestToolProps) => {
  const dispatch = useDispatch();

  const [annotation, setAnnotation] = useState<RegionOfInterestAnnotation>();
  const [havePoints, setHavePoints] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (havePoints && annotation) {
      if (
        Object.keys(LESION_LINE_TYPE_HIERARCHY[props.lesionLineType || ""])
          .length > 0
      ) {
        setOpenDialog(true); // Display dialog allowing choice of subtype
      } else {
        // Create the annotation immediately without a "select subtype" dialog
        props.onAnnotationComplete(annotation);
        dispatch(changeViewerMode(ViewerMode.PAN_ZOOM));
      }
    }
  }, [havePoints, annotation]);

  useHotkeys(
    "Return",
    () => {
      if (annotation && annotation.points.length > 2) {
        setHavePoints(true);
      }
    },
    [annotation]
  );

  useEffect(() => {
    const outsideImage = (point: Point) =>
      point.x < 0 ||
      point.y < 0 ||
      point.x > props.scaleInfo.width ||
      point.y > props.scaleInfo.height;

    const indexOfClickedPoint = (
      point: Point,
      points: Array<XYPoint>
    ): number | undefined => {
      if (points) {
        for (let i = 0; i < points.length; i++) {
          if (
            (point.x - points[i].x) ** 2 + (point.y - points[i].y) ** 2 <
            (THRESHOLD / props.scaleInfo.scaleX) ** 2
          ) {
            return i;
          }
        }
      }
      return undefined;
    };

    const clickHandlerId = props.viewerFacade.addHandler(
      "canvas-click",
      (event) => {
        const point = props.viewerFacade.imageCoords(event);
        if (point) {
          setAnnotation((annotation) => {
            if (!annotation) {
              if (!outsideImage(point)) {
                return createRegionOfInterestAnnotation(
                  undefined,
                  "",
                  props.user,
                  null,
                  [point],
                  props.lesionLineType,
                  undefined
                );
              }
            } else {
              const length = annotation.points.length;
              const index = indexOfClickedPoint(point, annotation.points);
              if (index !== undefined && index <= length - 2) {
                const possiblyTruncated = {
                  ...annotation,
                  points: annotation.points.slice(index, length),
                };
                setHavePoints(true);
                return possiblyTruncated;
              }
              if (index === undefined && !outsideImage(point)) {
                return extendRegionOfInterestAnnotation(annotation, point);
              }
            }
            return annotation;
          });
        }
      }
    );

    const doubleClickHandlerId = props.viewerFacade.addHandler(
      "canvas-double-click",
      (event) => {
        event.preventDefaultAction = true; // Prevent the default zoom
        // (Shouldn't be necessary with dblClickToZoom set to false)
        const point = props.viewerFacade.imageCoords(event);
        if (point) {
          setAnnotation((annotation) => {
            if (annotation) {
              const completed = {
                ...annotation,
                points: annotation.points.concat([point]),
              };
              setHavePoints(true);
              return completed;
            }
            return annotation;
          });
        }
      }
    );

    return () => {
      props.viewerFacade.removeHandler(clickHandlerId);
      props.viewerFacade.removeHandler(doubleClickHandlerId);
    };
  }, [
    props.scaleInfo.width,
    props.scaleInfo.height,
    props.viewerFacade,
    props.user,
  ]);

  return (
    <g
      x={0}
      y={0}
      width={props.scaleInfo.width}
      height={props.scaleInfo.height}
    >
      {annotation && (
        <>
          <RegionOfInterest
            annotation={annotation}
            scaleInfo={props.scaleInfo}
            viewerFacade={props.viewerFacade}
            isVisible
            closed={havePoints}
          />
          <Modal
            isOpen={openDialog}
            contentLabel="TYPE AND SUBTYPE"
            className="Modal"
            overlayClassName="Overlay"
          >
            <LesionLineTypeSelectionDialog
              annotation={annotation}
              lesionLineType={props.lesionLineType || ""}
              onAnnotationComplete={props.onAnnotationComplete}
            />
          </Modal>
        </>
      )}
    </g>
  );
};

export default RegionOfInterestTool;
